import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../constants';

export const DEFAULT_CONFIG_FORM = {
  taux_base: 0.012, taux_min: 0.008, taux_max: 0.018,
  tranches_taux_actives: false,
  tranches_taux: [
    { seuil_min: 0, seuil_max: 100000, taux: 0.018, label: 'Petits projets' },
    { seuil_min: 100001, seuil_max: 500000, taux: 0.015, label: 'Projets moyens' },
    { seuil_min: 500001, seuil_max: 1000000, taux: 0.012, label: 'Grands projets' },
    { seuil_min: 1000001, seuil_max: null, taux: 0.010, label: 'Tres grands projets' },
  ],
  coeff_territoire: { guadeloupe: 1.15, martinique: 1.15, guyane: 1.20, reunion: 1.10, mayotte: 1.25, saint_martin: 1.20, saint_barthelemy: 1.15 },
  affectation: { fonds_garantie: 0.60, prevention_expertise: 0.25, gestion_solidaire: 0.15 },
  // V37.31 — Coefficients d'operation, de batiment et reductions
  coeff_operation: { construction_neuve: 1.0, extension: 1.05, renovation_lourde: 1.15, rehabilitation: 1.20, surelevation: 1.25 },
  coeff_batiment: { individuel: 1.0, collectif: 1.10, mixte: 1.15, tertiaire: 1.20 },
  reduction_prevention: 0.05,
  reduction_ess: 0.10,
};

export default function useAdminConfig({ isSuperAdmin, headers, setConfig }) {
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [adminConfig, setAdminConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configForm, setConfigForm] = useState(DEFAULT_CONFIG_FORM);

  const fetchAdminConfig = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/config/admin`, { headers });
      if (res.data?.success) {
        setAdminConfig(res.data);
        const c = res.data.current_config;
        const defTranches = res.data.default_config?.tranches_taux || DEFAULT_CONFIG_FORM.tranches_taux;
        const defCoeff = res.data.default_config?.coeff_territoire || DEFAULT_CONFIG_FORM.coeff_territoire;
        setConfigForm({
          taux_base: c.taux_base || 0.012, taux_min: c.taux_min || 0.008, taux_max: c.taux_max || 0.018,
          tranches_taux_actives: c.tranches_taux_actives || false,
          tranches_taux: c.tranches_taux || defTranches,
          coeff_territoire: c.coeff_territoire || defCoeff,
          affectation: c.affectation || { fonds_garantie: 0.60, prevention_expertise: 0.25, gestion_solidaire: 0.15 },
          // V37.31
          coeff_operation: c.coeff_operation || res.data.default_config?.coeff_operation || DEFAULT_CONFIG_FORM.coeff_operation,
          coeff_batiment: c.coeff_batiment || res.data.default_config?.coeff_batiment || DEFAULT_CONFIG_FORM.coeff_batiment,
          reduction_prevention: c.reduction_prevention ?? res.data.default_config?.reduction_prevention ?? DEFAULT_CONFIG_FORM.reduction_prevention,
          reduction_ess: c.reduction_ess ?? res.data.default_config?.reduction_ess ?? DEFAULT_CONFIG_FORM.reduction_ess,
        });
      }
    } catch {}
  }, [headers, isSuperAdmin]);

  useEffect(() => { if (isSuperAdmin) fetchAdminConfig(); }, [isSuperAdmin, fetchAdminConfig]);

  const refreshPublicConfig = async () => {
    try {
      const configRes = await axios.get(`${BACKEND_URL}/api/domium/subscribe/config`);
      if (configRes.data?.success) setConfig(configRes.data.config);
    } catch {}
  };

  const saveAdminConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/domium/subscribe/config/admin`, configForm, { headers });
      if (res.data?.success) {
        toast.success('Configuration mise a jour');
        setShowConfigPanel(false);
        await refreshPublicConfig();
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur sauvegarde config'); }
    finally { setSavingConfig(false); }
  };

  const resetAdminConfig = async () => {
    if (!window.confirm('Reinitialiser la configuration aux valeurs par defaut ?')) return;
    setSavingConfig(true);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/domium/subscribe/config/admin`, { headers });
      if (res.data?.success) {
        toast.success('Configuration reinitialisee');
        setShowConfigPanel(false);
        fetchAdminConfig();
        await refreshPublicConfig();
      }
    } catch { toast.error('Erreur reinitialisation'); }
    finally { setSavingConfig(false); }
  };

  return {
    showConfigPanel, setShowConfigPanel,
    adminConfig, savingConfig, configForm, setConfigForm,
    saveAdminConfig, resetAdminConfig,
  };
}
